// Ship's Papers — one document: metadata, files (versions), preview, edit, delete.
// Also the create/edit form. Two fields are enough to file something (title + type);
// everything else is optional.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, EyeIcon, LinkIcon, PencilIcon, TrashIcon, XMarkIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import type { Catalog, PaperDoc, DocumentInput, Section, Audience, Member, EquipmentItem, DocFile, Role } from '../../types/papers';
import { AUDIENCE_LABEL, ROLE_RANK } from '../../types/papers';
import { btnPrimary, btnGhost, btnDanger, input, label, errMsg, fmtBytes, fmtDate, fmtAgo, ExpiryPill, Modal , PreviewPane, TakenDownNotice } from './shared';
import { PublishDocumentModal } from './SharedPapersPanel';
import { ShareLinksSection } from './ShareLinks';
import { extractPdfText } from './pdfText';
import { GlobeAltIcon } from '@heroicons/react/24/outline';

/** The document was saved; only its file failed. Carries the upload error. */
class UploadFailed extends Error {
  constructor(readonly uploadError: unknown) { super('upload failed'); }
}

export const typeLabel = (cat: Catalog | undefined, section: Section, type: string) => cat?.sections.find((s) => s.key === section)?.types.find((t) => t.key === type)?.label ?? type;

// ---------------- form ----------------

export function DocumentForm({ vid, catalog, members, equipment, initial, preset, myRole, onClose, onSaved }: {
  vid: string; catalog: Catalog; members: Member[]; equipment: EquipmentItem[]; initial: PaperDoc | null;
  preset?: Partial<DocumentInput>; myRole: Role; onClose: () => void; onSaved: (d: PaperDoc, wantsFile: boolean) => void;
}) {
  const isEdit = !!initial;
  const startSection = initial?.section ?? preset?.section ?? 'certificates';
  const [f, setF] = useState<DocumentInput>({
    // The type defaults here, not in an effect, so an untouched form is not "dirty".
    section: startSection, type: initial?.type ?? preset?.type ?? catalog.sections.find((s) => s.key === startSection)?.types[0]?.key ?? '', title: initial?.title ?? preset?.title ?? '',
    folder: initial?.folder ?? preset?.folder ?? '', issuer: initial?.issuer ?? '', reference_no: initial?.referenceNo ?? '', issued_on: initial?.issuedOn ?? '',
    expires_on: initial?.expiresOn ?? '', notes: initial?.notes ?? '', tags: initial?.tags ?? [], equipment_item_id: initial?.equipmentItemId ?? preset?.equipment_item_id ?? '',
    url: initial?.url ?? '', audience: initial?.audience ?? preset?.audience, audience_user_ids: initial?.audienceUserIds ?? [],
  });
  const [pristine] = useState(() => JSON.stringify(f));
  const [more, setMore] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  /** Created, but its file did not upload: a retry uploads to this one instead of creating another. */
  const [saved, setSaved] = useState<{ doc: PaperDoc; body: string } | null>(null);
  const types = catalog.sections.find((s) => s.key === f.section)?.types ?? [];
  const chosen = types.find((t) => t.key === f.type);
  const personal = f.section === 'crew';
  const canChooseSection = !isEdit && !preset?.section;
  const crewOnly = myRole === 'crew' || myRole === 'guest';

  useEffect(() => { if (!types.some((t) => t.key === f.type)) setF((x) => ({ ...x, type: types[0]?.key ?? '' })); }, [f.section]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: async () => {
      const body: DocumentInput = {
        ...f, title: f.title.trim(), folder: f.folder?.trim() || null, issuer: f.issuer?.trim() || null, reference_no: f.reference_no?.trim() || null,
        issued_on: f.issued_on || null, expires_on: f.expires_on || null, notes: f.notes?.trim() || null, url: f.url?.trim() || null,
        equipment_item_id: f.equipment_item_id || null, audience: personal ? undefined : f.audience,
      };
      const sent = JSON.stringify(body);
      const d = saved
        ? (saved.body === sent ? saved.doc : await api.papersUpdateDocument(vid, saved.doc.id, body))
        : isEdit ? await api.papersUpdateDocument(vid, initial!.id, body) : await api.papersCreateDocument(vid, body);
      if (file) {
        try { await api.papersUploadFile(vid, d.id, file, f.section === 'crew' ? null : await extractPdfText(file)); }
        catch (e) { setSaved({ doc: d, body: sent }); throw new UploadFailed(e); }
      }
      return d;
    },
    onSuccess: (d) => { setError(null); onSaved(d, !file && !isEdit); },
    // Upload errors (413 quota, 415 type) are not toasted by the api client — this is where they are said.
    onError: (e) => setError(e instanceof UploadFailed
      ? `${errMsg(e.uploadError, 'The file could not be uploaded.')} The document was saved without its file — try again, choose another file, or close and add the file later.`
      : errMsg(e, 'Could not save the document.')),
  });
  /** Once the document exists, closing shows it (without its file) rather than pretending nothing happened. */
  const close = () => (saved ? onSaved(saved.doc, false) : onClose());

  const dirty = !!file || JSON.stringify(f) !== pristine;
  const namedMembers = members.filter((m) => m.status === 'active' && m.userId && ROLE_RANK[m.role] < ROLE_RANK.captain);

  return (
    <Modal title={isEdit ? 'Edit document' : personal ? 'Add one of your own papers' : 'Add a document'} onClose={close} dirty={dirty}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {canChooseSection && (
          <label className={label}>Section
            <select className={`${input} mt-1`} value={f.section} onChange={(e) => setF({ ...f, section: e.target.value as Section })}>
              {catalog.sections.filter((s) => !crewOnly || s.key === 'crew').map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        )}
        <label className={label}>What is it?
          <select className={`${input} mt-1`} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
            {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </label>
        <label className={label}>Title
          <input className={`${input} mt-1`} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={chosen ? `e.g. ${chosen.label} 2026` : 'e.g. Pantaenius policy 2026'} maxLength={200} autoFocus />
        </label>
        {chosen?.expires && (
          <label className={label}>Expires on {chosen.expected && <span className="text-slate-400 font-normal">(drives Ready to sail)</span>}
            <input type="date" className={`${input} mt-1`} value={f.expires_on ?? ''} onChange={(e) => setF({ ...f, expires_on: e.target.value })} />
          </label>
        )}
        {!isEdit && (
          <div>
            <span className={label}>File <span className="text-slate-400 font-normal">(PDF or photo — you can add it later)</span></span>
            <div className="flex gap-2 mt-1">
              <input type="file" accept="application/pdf,image/*" className={`${input} file:mr-3 file:rounded-md file:border-0 file:bg-slate-700 file:text-white file:px-3 file:py-1`} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              <label className={`${btnGhost} cursor-pointer shrink-0`} title="Take a photo of the paper document (phone or tablet)">
                📷 Photo<input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              </label>
            </div>
            {file && <p className="text-sm text-slate-400 mt-1">{file.name} · {(file.size / 1048576).toFixed(1)} MB</p>}
          </div>
        )}
        {!more ? (
          <button type="button" onClick={() => setMore(true)} className="text-sm text-amber-300 hover:underline">More details (issuer, reference, folder, notes, who can see it)</button>
        ) : (<>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className={label}>Issued by<input className={`${input} mt-1`} value={f.issuer ?? ''} onChange={(e) => setF({ ...f, issuer: e.target.value })} maxLength={120} /></label>
            <label className={label}>Reference / policy no.<input className={`${input} mt-1`} value={f.reference_no ?? ''} onChange={(e) => setF({ ...f, reference_no: e.target.value })} maxLength={120} /></label>
            <label className={label}>Issued on<input type="date" className={`${input} mt-1`} value={f.issued_on ?? ''} onChange={(e) => setF({ ...f, issued_on: e.target.value })} /></label>
            {!chosen?.expires && <label className={label}>Expires on<input type="date" className={`${input} mt-1`} value={f.expires_on ?? ''} onChange={(e) => setF({ ...f, expires_on: e.target.value })} /></label>}
          </div>
          {!personal && (
            <label className={label}>Folder <span className="text-slate-400 font-normal">(optional, e.g. "Fiji 2026")</span>
              <input className={`${input} mt-1`} value={f.folder ?? ''} onChange={(e) => setF({ ...f, folder: e.target.value })} maxLength={80} />
            </label>
          )}
          {!personal && f.section === 'equipment' && equipment.length > 0 && (
            <label className={label}>Equipment item
              <select className={`${input} mt-1`} value={f.equipment_item_id ?? ''} onChange={(e) => setF({ ...f, equipment_item_id: e.target.value })}>
                <option value="">— none —</option>
                {equipment.map((e) => <option key={e.id} value={e.id}>{e.name}{e.model ? ` · ${e.model}` : ''}</option>)}
              </select>
            </label>
          )}
          <label className={label}>Link <span className="text-slate-400 font-normal">(optional — a web page or manufacturer PDF)</span>
            <input type="url" className={`${input} mt-1`} value={f.url ?? ''} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://" />
          </label>
          <label className={label}>Notes<textarea rows={2} className={`${input} mt-1`} value={f.notes ?? ''} onChange={(e) => setF({ ...f, notes: e.target.value })} maxLength={4000} /></label>
          {!personal && (
            <fieldset>
              <legend className={label}>Who can see it</legend>
              <div className="flex flex-wrap gap-2 mt-1">
                {(['all', 'officers', 'named'] as Audience[]).map((a) => (
                  <button key={a} type="button" aria-pressed={f.audience === a} onClick={() => setF({ ...f, audience: a })}
                    className={`px-3 py-1.5 rounded-full border text-sm ${f.audience === a ? 'border-amber-400 text-amber-300 bg-amber-400/10' : 'border-slate-600 text-slate-300'}`}>{AUDIENCE_LABEL[a]}</button>
                ))}
              </div>
              {f.audience === 'named' && (
                <div className="mt-2 grid sm:grid-cols-2 gap-1.5">
                  {namedMembers.length === 0 && <p className="text-sm text-slate-400">Nobody else is aboard yet — the owner and captain always see everything.</p>}
                  {namedMembers.map((m) => (
                    <label key={m.id} className="flex items-center gap-2 text-sm text-slate-200">
                      <input type="checkbox" className="w-4 h-4 rounded bg-slate-700 border-slate-600 text-amber-400" checked={(f.audience_user_ids ?? []).includes(m.userId as string)}
                        onChange={(e) => setF({ ...f, audience_user_ids: e.target.checked ? [...(f.audience_user_ids ?? []), m.userId as string] : (f.audience_user_ids ?? []).filter((x) => x !== m.userId) })} />
                      {m.userName ?? m.userEmail}{m.title ? ` · ${m.title}` : ''}
                    </label>
                  ))}
                </div>
              )}
            </fieldset>
          )}
        </>)}
        {error && <p role="alert" className="text-sm font-medium text-red-300 bg-red-900/30 border border-red-800/60 rounded-lg px-3 py-2">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={close} className={btnGhost}>{saved ? 'Close' : 'Cancel'}</button>
          <button type="submit" disabled={save.isPending || !f.title.trim() || !f.type || (!!saved && !file)} className={btnPrimary}>{save.isPending ? 'Saving…' : saved ? 'Upload the file again' : isEdit ? 'Save' : 'Add document'}</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------- panel ----------------

export function DocumentPanel({ vid, docId, catalog, members, equipment, myRole, myUserId, onClose, onChanged }: {
  vid: string; docId: string; catalog: Catalog; members: Member[]; equipment: EquipmentItem[]; myRole: Role; myUserId: string; onClose: () => void; onChanged: () => void;
}) {
  const qc = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [preview, setPreview] = useState<{ url: string; mime: string; name: string } | null>(null);
  /** Which document the open preview belongs to — lets the viewer follow the selection. */
  const previewDocRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: doc, isLoading } = useQuery({ queryKey: ['papers-doc', vid, docId], queryFn: () => api.papersGetDocument(vid, docId) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['papers-doc', vid, docId] }); qc.invalidateQueries({ queryKey: ['papers-shares', vid, docId] }); onChanged(); };
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);

  const personal = !!doc && doc.section === 'crew' && !doc.vesselId;
  const canEdit = !!doc && (personal ? doc.ownerUserId === myUserId : ROLE_RANK[myRole] >= ROLE_RANK.officer);
  const canPublish = !!doc && !personal && doc.origin !== 'library' && ROLE_RANK[myRole] >= ROLE_RANK.captain;

  const upload = useMutation({
    mutationFn: async (file: File) => api.papersUploadFile(vid, docId, file, doc && doc.section === 'crew' && !doc.vesselId ? null : await extractPdfText(file)),
    onSuccess: (f) => { refresh(); addToast({ type: 'success', title: f.version > 1 ? `Version ${f.version} added` : 'File added' }); },
    onError: (e) => setError(errMsg(e, 'Could not upload the file.')),
  });
  const del = useMutation({
    mutationFn: () => api.papersDeleteDocument(vid, docId),
    onSuccess: () => { addToast({ type: 'success', title: 'Document deleted' }); onChanged(); onClose(); },
    onError: (e) => { setConfirmDelete(false); setError(errMsg(e, 'Could not delete the document.')); },
  });

  const open = async (f: DocFile, download: boolean) => {
    try {
      const blob = await api.papersFileBlob(vid, docId, f.id, download);
      const url = URL.createObjectURL(blob);
      if (download) { const a = document.createElement('a'); a.href = url; a.download = f.fileName; a.click(); setTimeout(() => URL.revokeObjectURL(url), 10_000); }
      else { if (preview) URL.revokeObjectURL(preview.url); previewDocRef.current = docId; setPreview({ url, mime: f.mime, name: f.fileName }); }
    } catch (e) { setError(errMsg(e, 'Could not open the file.')); }
  };

  const files = useMemo(() => doc?.files ?? [], [doc]);

  // The viewer follows the selection: with a preview open, picking another
  // document in the list loads THAT document's latest file (or closes the
  // viewer when the new document has no file) instead of showing the old one.
  useEffect(() => {
    if (!preview || !doc || previewDocRef.current === doc.id) return;
    const top = doc.files?.[0];
    if (top) { void open(top, false); }
    else { URL.revokeObjectURL(preview.url); previewDocRef.current = null; setPreview(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id, preview]);

  return (
    <aside className="bg-slate-800/70 border border-slate-700 rounded-2xl p-4 flex flex-col gap-3 min-h-[420px]" aria-label="Document details">
      {isLoading || !doc ? <div className="text-slate-400">Loading…</div> : (<>
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-400">{typeLabel(catalog, doc.section, doc.type)}{doc.folder ? ` · ${doc.folder}` : ''}{personal && doc.ownerName ? ` · ${doc.ownerName}` : ''}</div>
            <h2 className="text-lg font-bold text-white leading-tight break-words">{doc.title}</h2>
            <div className="mt-1 flex flex-wrap gap-2 items-center"><ExpiryPill expiresOn={doc.expiresOn} />
              {!personal && <span className="text-[13px] text-slate-400 border border-slate-600 rounded-full px-2 py-0.5">{AUDIENCE_LABEL[doc.audience]}</span>}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-slate-400 hover:text-white"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        {doc.sourceLibraryTakenDown && <TakenDownNotice />}

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          {doc.issuer && <><dt className="text-slate-400">Issued by</dt><dd className="text-white">{doc.issuer}</dd></>}
          {doc.referenceNo && <><dt className="text-slate-400">Reference</dt><dd className="text-white break-all">{doc.referenceNo}</dd></>}
          {doc.issuedOn && <><dt className="text-slate-400">Issued</dt><dd className="text-white">{fmtDate(doc.issuedOn)}</dd></>}
          {doc.expiresOn && <><dt className="text-slate-400">Expires</dt><dd className="text-white">{fmtDate(doc.expiresOn)}</dd></>}
          <dt className="text-slate-400">Updated</dt><dd className="text-white">{fmtAgo(doc.updatedAt)}</dd>
        </dl>
        {doc.notes && <p className="text-sm text-slate-300 whitespace-pre-line">{doc.notes}</p>}
        {doc.url && <a href={doc.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-amber-300 hover:underline break-all"><LinkIcon className="w-4 h-4 shrink-0" />{doc.url}</a>}

        <section>
          <div className="flex items-center justify-between"><h3 className="text-sm font-semibold text-slate-200">Files</h3>
            {canEdit && (<>
              <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }} />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={upload.isPending} className={btnGhost}><ArrowUpTrayIcon className="w-4 h-4" />{upload.isPending ? 'Uploading…' : files.length ? 'New version' : 'Add file'}</button>
            </>)}
          </div>
          {files.length === 0 ? <p className="text-sm text-slate-400 mt-1">No file yet{doc.url ? ' — this is a link' : ''}.</p> : (
            <ul className="mt-1 divide-y divide-slate-700/70">
              {files.map((f) => (
                <li key={f.id} className="flex items-center gap-2 py-2 text-sm">
                  <DocumentIcon className="w-5 h-5 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0"><div className="text-white truncate">{f.fileName}</div><div className="text-slate-400">v{f.version} · {fmtBytes(f.sizeBytes)} · {fmtAgo(f.createdAt)}</div></div>
                  <button type="button" onClick={() => open(f, false)} className={btnGhost} aria-label={`Preview ${f.fileName}`}><EyeIcon className="w-4 h-4" /></button>
                  <button type="button" onClick={() => open(f, true)} className={btnGhost} aria-label={`Download ${f.fileName}`}><ArrowDownTrayIcon className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {preview && <PreviewPane url={preview.url} mime={preview.mime} name={preview.name} onClose={() => { URL.revokeObjectURL(preview.url); setPreview(null); }} />}

        {error && <p role="alert" className="text-sm font-medium text-red-300 bg-red-900/30 border border-red-800/60 rounded-lg px-3 py-2">{error}</p>}

        {canEdit && files.length > 0 && <ShareLinksSection vid={vid} docId={docId} />}

        {canEdit && (
          <div className="flex gap-2 mt-auto pt-2">
            <button type="button" onClick={() => setEditing(true)} className={btnGhost}><PencilIcon className="w-4 h-4" /> Edit</button>
            {canPublish && <button type="button" onClick={() => setPublishing(true)} className={btnGhost}><GlobeAltIcon className="w-4 h-4" /> Publish to shared library</button>}
            <button type="button" onClick={() => setConfirmDelete(true)} className={`${btnDanger} ml-auto`}><TrashIcon className="w-4 h-4" /> Delete</button>
          </div>
        )}

        {publishing && <PublishDocumentModal documentIds={[doc.id]} section={doc.section as import('../../types/papers').PaperLibrarySection} label={`“${doc.title}”`} catalog={catalog} onClose={() => setPublishing(false)} />}
        {editing && <DocumentForm vid={vid} catalog={catalog} members={members} equipment={equipment} initial={doc} myRole={myRole} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); refresh(); }} />}
        {confirmDelete && (
          <Modal title={`Delete “${doc.title}”?`} onClose={() => setConfirmDelete(false)}>
            <p className="text-base text-slate-300">All {files.length} file version{files.length === 1 ? '' : 's'} go with it. This cannot be undone.</p>
            <div className="flex justify-end gap-2"><button type="button" onClick={() => setConfirmDelete(false)} className={btnGhost}>Cancel</button><button type="button" onClick={() => del.mutate()} disabled={del.isPending} className={`${btnPrimary} !bg-red-500 !text-white`}>{del.isPending ? 'Deleting…' : 'Delete'}</button></div>
          </Modal>
        )}
      </>)}
    </aside>
  );
}
